// =============================================================================
// TrackerPage.jsx
//
// Alterações relativamente à v1:
//   1. Estado de filtros (filters) — filtragem puramente local, sem chamadas API
//   2. useMemo → filteredEntries — entries após aplicar todos os filtros activos
//   3. Renderiza TrackerFilters acima da tabela
//   4. Renderiza TrackerGantt abaixo da tabela
//
// Lógica de filtragem temporal:
//   From → StartDate >= From
//   To   → StartDate <= To
// =============================================================================

import React, { useState, useEffect, useReducer, useCallback, useRef, useMemo } from 'react';
import { useSelector } from 'react-redux';
import {
  Box, Button, Typography, Alert, Snackbar,
  Stack, Chip, CircularProgress, Tooltip, IconButton,
  FormControlLabel, Switch,
} from '@mui/material';
import AddIcon            from '@mui/icons-material/Add';
import TuneIcon           from '@mui/icons-material/Tune';
import DownloadIcon       from '@mui/icons-material/Download';
import TimelineIcon       from '@mui/icons-material/Timeline';
import CalendarMonthIcon  from '@mui/icons-material/CalendarMonth';

import TrackerTable    from './TrackerTable';
import TrackerModal    from './TrackerModal';
import SiteListDialog  from './SiteListDialog';
import TrackerFilters  from './TrackerFilters';
import TrackerGantt    from './TrackerGantt';
import TrackerCalendar  from './TrackerCalendar';
import TrackerSettings  from './TrackerSettings';
import BulkImportDialog from './BulkImportDialog';

import trackerReducer                                  from '../reducer/trackerReducer';
import { loadEntries, loadEntry, saveEntry, removeEntry } from '../reducer/trackerThunks';
import { openModal, closeModal, clearError }           from '../actions/trackerActions';
import { apiExportAll, apiGetFeatures, apiGetPrivilege } from '../requests/trackerRequests';
import { apiCanImport } from '../requests/importRequests';
import UploadFileIcon from '@mui/icons-material/UploadFile';


// =============================================================================
// useThunkReducer — suporta thunks (funções) além de actions (objectos)
// =============================================================================
const useThunkReducer = (reducer, initialState) => {
  const [state, rawDispatch] = useReducer(reducer, initialState);

  const dispatch = useCallback(
    (action) => {
      if (typeof action === 'function') {
        action(dispatch);
      } else {
        rawDispatch(action);
      }
    },
    [rawDispatch]
  );

  return [state, dispatch];
};


// ── State inicial do reducer ──────────────────────────────────────────────────
const initialState = {
  entries:     [],
  activeEntry: null,
  loading:     false,
  saving:      false,
  loadingOne:  false,
  error:       null,
  modalOpen:   false,
};

// ── State inicial dos filtros ─────────────────────────────────────────────────
const emptyFilters = {
  dateFrom:     null,   // 'yyyy-MM-dd' ou null
  dateTo:       null,   // 'yyyy-MM-dd' ou null
  technologies: [],     // string[]
  teams:        [],     // string[]
  owners:       [],     // string[]
  features:     [],     // string[]
  vendors:      [],     // string[]
  sides:        [],     // string[] — 'East' | 'West' | 'None' (ver SIDE_BUCKET)
};

// Side (TFinTF/TFinVF/VFinTF, guardado por site em server.tacs[].siteSides)
// agrupado em dois "buckets" de negócio, por especificação:
//   West → TFinVF
//   East → TFinTF e VFinTF
// Uma atividade pode ter sites de vários buckets ao mesmo tempo (um único
// OMC_ID pode ter sites com Side diferente — já confirmado por SQL), por
// isso o filtro é "a atividade tem pelo menos um site neste bucket", não
// "todos os sites são deste bucket".
const SIDE_BUCKET = { TFinTF: 'East', VFinTF: 'East', TFinVF: 'West' };

const getEntrySideBuckets = (entry) => {
  const buckets = new Set();
  (entry.servers ?? []).forEach((s) =>
    (s.tacs ?? []).forEach((t) =>
      Object.values(t.siteSides ?? {}).forEach((side) => {
        const bucket = SIDE_BUCKET[side];
        if (bucket) buckets.add(bucket);
      })
    )
  );
  return buckets;
};

// 'None' e um valor sentinela (nao vem de SIDE_BUCKET): representa
// atividades sem NENHUM site com Side resolvido (sem servers, ou servers/
// sites sem Side gravado). Pedido do utilizador para poder isolar estes
// casos no filtro, tal como ja aparecem destacados ("—") na tabela.
const entryHasSideValue = (entry, value) => {
  const buckets = getEntrySideBuckets(entry);
  return value === 'None' ? buckets.size === 0 : buckets.has(value);
};

const initialSnackbar = { open: false, message: '', severity: 'success' };


// =============================================================================
// Helpers de filtragem
// =============================================================================

// Converte 'yyyy-MM-dd' → timestamp (início do dia UTC)
const toMs = (dateStr) => dateStr ? new Date(dateStr + 'T00:00:00Z').getTime() : null;

// Converte string de data da API (vários formatos) → timestamp UTC
const parseDateMs = (raw) => {
  if (!raw) return null;
  const s = String(raw).trim();

  const slash = s.match(/^(\d{2})\/(\d{2})\/(\d{4})/);
  if (slash) return new Date(`${slash[3]}-${slash[1]}-${slash[2]}T00:00:00Z`).getTime();

  const dash = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (dash) {
    const [, y, a, b] = dash;
    const iso = Number(a) > 12 ? `${y}-${b}-${a}` : `${y}-${a}-${b}`;
    return new Date(`${iso}T00:00:00Z`).getTime();
  }

  const d = new Date(s);
  return isNaN(d.getTime()) ? null : d.getTime();
};

// Aplica todos os filtros activos a um array de entries
// excludeKey (opcional) — ignora o próprio filtro desse campo, mantendo os
// restantes activos. Usado por deriveFilterOptions para calcular, para cada
// dropdown, só as opções que continuam possíveis dado o que já está
// seleccionado nos OUTROS filtros (faceted search / cascata entre todos os
// filtros, incluindo o Side).
const applyFilters = (entries, filters, excludeKey = null) => {
  const filtFrom = excludeKey === 'dateRange' ? null : toMs(filters.dateFrom);
  const filtTo   = excludeKey === 'dateRange' ? null : toMs(filters.dateTo);

  return entries.filter((e) => {
    // ── Filtro de datas ───────────────────────────────────────────────────────
    if (filtFrom || filtTo) {
      const actStart = parseDateMs(e.startDate);

      if (!actStart) return false;

      // From → StartDate >= From
      if (filtFrom && actStart < filtFrom) return false;

      // To   → StartDate <= To
      if (filtTo && actStart > filtTo) return false;
    }

    // ── Filtro de tecnologia ──────────────────────────────────────────────────
    if (excludeKey !== 'technologies' && filters.technologies.length > 0) {
      if (!filters.technologies.includes(e.technology)) return false;
    }

    // ── Filtro de equipa ──────────────────────────────────────────────────────
    if (excludeKey !== 'teams' && filters.teams.length > 0) {
      if (!filters.teams.includes(e.teamName)) return false;
    }

    // ── Filtro de owner ───────────────────────────────────────────────────────
    if (excludeKey !== 'owners' && filters.owners.length > 0) {
      const entryOwners = e.ownerNames ?? [];
      const hasMatch = entryOwners.some((o) => filters.owners.includes(o));
      if (!hasMatch) return false;
    }

    // ── Filtro de feature ─────────────────────────────────────────────────────
    if (excludeKey !== 'features' && filters.features.length > 0) {
      if (!filters.features.includes(e.feature)) return false;
    }

    if (excludeKey !== 'vendors' && filters.vendors.length > 0) {
      if (!filters.vendors.includes(e.vendor)) return false;
    }

    // ── Filtro de Side (East/West/None) ─────────────────────────────────────
    if (excludeKey !== 'sides' && filters.sides.length > 0) {
      const hasMatch = filters.sides.some((b) => entryHasSideValue(e, b));
      if (!hasMatch) return false;
    }

    return true;
  });
};

// Valores fixos do dropdown Side (classificação de negócio, não derivada
// dos dados — ver SIDE_BUCKET acima).
const SIDE_VALUES = ['East', 'West', 'None'];

// Deriva, para cada dropdown, só as opções que ainda fazem sentido dado o
// que já está seleccionado nos RESTANTES filtros (cada campo é calculado
// ignorando o seu próprio filtro, para nunca ficar preso incapaz de alargar
// de novo). Ex: se Side=West já estiver escolhido e não houver nenhuma
// atividade 2G do lado West, "2G" deixa de aparecer no Technology.
const deriveFilterOptions = (entries, filters) => {
  const techs    = [...new Set(applyFilters(entries, filters, 'technologies').map((e) => e.technology).filter(Boolean))].sort();
  const teams    = [...new Set(applyFilters(entries, filters, 'teams').map((e) => e.teamName).filter(Boolean))].sort();
  const owners   = [...new Set(applyFilters(entries, filters, 'owners').flatMap((e) => e.ownerNames ?? []).filter(Boolean))].sort();
  const features = [...new Set(applyFilters(entries, filters, 'features').map((e) => e.feature).filter(Boolean))].sort();
  const vendors  = [...new Set(applyFilters(entries, filters, 'vendors').map((e) => e.vendor).filter(Boolean))].sort();

  const sidesPool = applyFilters(entries, filters, 'sides');
  const sides = SIDE_VALUES.filter((s) => sidesPool.some((e) => entryHasSideValue(e, s)));

  // From/To — em vez de opções discretas, o "que ainda faz sentido" é o
  // intervalo [min, max] de StartDate das atividades que já passam nos
  // restantes filtros. O DatePicker usa isto para limitar o que dá para
  // escolher (mesmo princípio da cascata, adaptado a um campo contínuo).
  const dateStarts = applyFilters(entries, filters, 'dateRange')
    .map((e) => parseDateMs(e.startDate))
    .filter((ms) => ms != null);
  const dateBounds = dateStarts.length
    ? {
        min: new Date(Math.min(...dateStarts)).toISOString().slice(0, 10),
        max: new Date(Math.max(...dateStarts)).toISOString().slice(0, 10),
      }
    : { min: null, max: null };

  return { techs, teams, owners, features, vendors, sides, dateBounds };
};


// =============================================================================
// COMPONENTE
// =============================================================================
const TrackerPage = () => {
  const [state, dispatch] = useThunkReducer(trackerReducer, initialState);
  const { entries, activeEntry, loading, saving, loadingOne, error, modalOpen } = state;

  // userId do utilizador autenticado — enviado para a API para o audit log
  const userId = useSelector((s) => s.session.user.id);

  // ── Privilege (role + vendor + module access) ─────────────────────────────
  const [privilege, setPrivilege] = useState(null);   // null = still loading
  const role      = privilege?.role ?? null;
  const canCreate = role === null || role === 'Admin' || role === 'Regular';

  // Admin pode sempre editar/apagar. Todos os outros (exceto Tourist, que é
  // sempre read-only) só podem editar/apagar se forem o Criador OU um dos
  // Owners da atividade — tem de espelhar exatamente a regra do backend
  // (EnforceWriteAccess em ActivityTrackerController.cs).
  const isCreatorOfEntry = (entry) => String(entry?.createdBy) === String(userId);
  const isOwnerOfEntry   = (entry) => (entry?.ownerUserIds ?? []).map(String).includes(String(userId));
  const canModifyEntry   = (entry) =>
    role === null ||
    role === 'Admin' ||
    (role !== 'Tourist' && (isCreatorOfEntry(entry) || isOwnerOfEntry(entry)));

  const canEditEntry   = canModifyEntry;
  const canDeleteEntry = canModifyEntry;
  const vendor         = privilege?.vendor ?? 'Both';

  const [snackbar, setSnackbar] = useState(initialSnackbar);
  const [filters,  setFilters]  = useState(emptyFilters);
  const [showClosed, setShowClosed] = useState(false);
  const [featureTypes, setFeatureTypes] = useState([]);  // [{ id, name, color }]
  const [chartView, setChartView] = useState('gantt');   // 'gantt' | 'calendar'
  const [settingsOpen,   setSettingsOpen]   = useState(false);
  const [lookupsVersion, setLookupsVersion] = useState(0);  // incremented when Settings closes

  // Bulk Import — button only shows for Admins or whoever is listed in
  // TrackerImportAuthorizedUser (see ActivityImportController.CanImport).
  const [canImport, setCanImport]           = useState(false);
  const [bulkImportOpen, setBulkImportOpen] = useState(false);

  useEffect(() => {
    apiGetFeatures()
      .then((res) => setFeatureTypes(res.data ?? []))
      .catch(() => {});
    apiCanImport()
      .then((res) => setCanImport(!!res.data?.canImport))
      .catch(() => setCanImport(false));
  }, []);

  // ── SiteListDialog — fora do TrackerModal para evitar bug de backdrops ────
  const [siteListOpen,     setSiteListOpen]     = useState(false);
  const [siteListServer,   setSiteListServer]   = useState(null);
  const [siteListCallback, setSiteListCallback] = useState(null);


  // ── Entries filtradas (memo — só recalcula quando entries ou filters mudam) ─
  const filteredEntries = useMemo(
    () => applyFilters(entries, filters),
    [entries, filters]
  );

  // ── Opções de cada dropdown, em cascata com os restantes filtros activos ──
  const filterOptions = useMemo(
    () => deriveFilterOptions(entries, filters),
    [entries, filters]
  );


  // ── Snackbar helpers ──────────────────────────────────────────────────────
  const showSnack  = (message, severity = 'success') =>
    setSnackbar({ open: true, message, severity });
  const closeSnack = () =>
    setSnackbar((prev) => ({ ...prev, open: false }));


  // ── Carregar tabela ao montar ─────────────────────────────────────────────
  useEffect(() => {
    dispatch(loadEntries(vendor));

    // Fetch privilege for this user
    if (userId) {
      apiGetPrivilege(userId)
        .then((res) => {
          const data = res.data;
          if (!data) { setPrivilege({ role: 'Tourist', vendor: 'Both' }); return; }
          const hasAccess = data.moduleAccess?.find((m) => m.moduleKey === 'activitiesTracker')?.hasAccess;
          setPrivilege({
            role:   hasAccess ? (data.role ?? 'Tourist') : 'Tourist',
            vendor: data.vendor ?? 'Both',
          });
        })
        .catch(() => setPrivilege({ role: 'Tourist', vendor: 'Both' }));
    }
  }, []);


  // ── Snackbar após operações de escrita ────────────────────────────────────
  const prevSaving     = useRef(false);
  const lastOpIsDelete = useRef(false);
  useEffect(() => {
    if (prevSaving.current && !saving) {
      if (error) {
        // Popup de erro (ex: "You don't have permission to edit or delete
        // this activity...") em vez de deixar o utilizador só com o banner
        // no topo da página — mais visível para ações pontuais como Delete.
        showSnack(error, 'error');
      } else {
        showSnack(
          lastOpIsDelete.current ? 'Activity deleted.' : 'Activity saved successfully.',
          lastOpIsDelete.current ? 'info' : 'success'
        );
      }
      lastOpIsDelete.current = false;
    }
    prevSaving.current = saving;
  }, [saving, error]);  // eslint-disable-line react-hooks/exhaustive-deps


  // ── Handlers da tabela e modal ────────────────────────────────────────────

  const handleRowClick = (row) => {
    const normalizedRow = {
      ...row,
      teamId:  row.teamId  != null ? String(row.teamId)  : '',
      ownerId: row.ownerId != null ? String(row.ownerId) : '',
    };
    dispatch(openModal(normalizedRow));
    dispatch(loadEntry(row.id));
  };

  const handleAddNew     = ()         => dispatch(openModal(null));

  const handleCopy = (entry) => {
    dispatch(openModal({
      ...entry,
      id:        undefined,
      title:     `${entry.title} - Copy`,
      startDate: '',
      endDate:   '',
      phases:    [],
      comments:  '',
      status:    'Open',
    }));
  };
  const handleCloseModal = ()         => dispatch(closeModal());
  const handleSave = (formData) => {
    lastOpIsDelete.current = false;
    dispatch(saveEntry(formData, userId));
  };

  const handleDeleteFromTable = (id, entryTitle) => {
    if (!window.confirm('Remove this activity? This cannot be undone.')) return;
    lastOpIsDelete.current = true;
    dispatch(removeEntry(id, userId, entryTitle));
  };

  const handleDeleteFromModal = (id, entryTitle) => {
    lastOpIsDelete.current = true;
    dispatch(removeEntry(id, userId, entryTitle));
  };


  // ── Handlers do SiteListDialog ────────────────────────────────────────────

  const handleOpenSiteList = (server, updateServerFn) => {
    setSiteListServer(server);
    setSiteListCallback(() => updateServerFn);
    setSiteListOpen(true);
  };

  const handleCloseSiteList = () => {
    setSiteListOpen(false);
    setTimeout(() => {
      setSiteListServer(null);
      setSiteListCallback(null);
    }, 300);
  };

  const handleConfirmSiteList = (selectedSites) => {
    if (siteListCallback) siteListCallback(selectedSites);
    handleCloseSiteList();
  };


  // ── Chips de contagem por tecnologia (sobre as entries filtradas) ─────────
  const techCounts = ['2G', '3G', '4G', '5G']
    .map((t) => ({ t, n: filteredEntries.filter((e) => e.technology === t && (showClosed || e.status !== 'Closed')).length }))
    .filter((x) => x.n > 0);

  const displayedEntries = showClosed ? filteredEntries : filteredEntries.filter((e) => e.status !== 'Closed');
  const isFiltered = displayedEntries.length < (showClosed ? entries : entries.filter(e => e.status !== 'Closed')).length;


  // ── Cabeçalho + barra de filtros ── extraído para poder ser reenviado
  // também para dentro do diálogo fullscreen da tabela (botão "Maximize"),
  // exactamente como aparece na página normal (mesmas permissões, mesmos
  // handlers, sem duplicar lógica).
  const pageToolbar = (
    <>
      {/* ── Cabeçalho ──────────────────────────────────────────────────────── */}
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 3 }}>
        <Box>
          <Typography variant="h5" fontWeight={700} letterSpacing={-0.5} color="text.primary">
            Network Activity Tracker
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.25, display: 'block' }}>
            {isFiltered
              ? <><strong>{displayedEntries.length}</strong> of {(showClosed ? entries : entries.filter(e => e.status !== 'Closed')).length} activities · {techCounts.map(({t,n}) => `${t}: ${n}`).join(' · ')}</>
              : <>{displayedEntries.length} {displayedEntries.length === 1 ? 'activity' : 'activities'} · {techCounts.map(({t,n}) => `${t}: ${n}`).join(' · ')}</>
            }
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          {loadingOne && <CircularProgress size={20} />}
          <FormControlLabel
            control={<Switch size="small" checked={showClosed} onChange={(e) => setShowClosed(e.target.checked)} />}
            label={<Typography variant="caption" color="text.secondary">Show Closed</Typography>}
            labelPlacement="start"
            sx={{ mr: 0 }}
          />
          <Tooltip title="Export all activities to Excel">
            <span>
              <Button
                variant="outlined" startIcon={<DownloadIcon />}
                onClick={() => apiExportAll()}
                disabled={entries.length === 0}
                disableElevation
                size="small"

              >
                Export All
              </Button>
            </span>
          </Tooltip>
          {(role === 'Admin' || role === 'Manager') && (
            <Button
              variant="outlined" startIcon={<TuneIcon />}
              onClick={() => setSettingsOpen(true)}
              disableElevation size="small"
            >
              Settings
            </Button>
          )}
          {canImport && (
            <Button
              variant="outlined" startIcon={<UploadFileIcon />}
              onClick={() => setBulkImportOpen(true)}
              disableElevation size="small"
            >
              Bulk Import
            </Button>
          )}
          <Button variant="contained" startIcon={<AddIcon />} onClick={handleAddNew} disableElevation size="small"
            disabled={!canCreate}
            title={!canCreate ? 'You do not have permission to create activities' : undefined}
          >
            New Activity
          </Button>
        </Box>
      </Stack>

      {/* ── Banner de erro global ───────────────────────────────────────────── */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => dispatch(clearError())}>
          {error}
        </Alert>
      )}

      {/* ── Barra de filtros ────────────────────────────────────────────────── */}
      <TrackerFilters
        entries={entries}
        options={filterOptions}
        filters={filters}
        onChange={setFilters}
        onClear={() => setFilters(emptyFilters)}
        filtered={filteredEntries.length}
      />
    </>
  );

  // Legenda dos Types — extraída para poder ser reenviada também para
  // dentro do fullscreen do Gantt (mesma lógica do maximizedHeader da
  // tabela: um único bloco reaproveitado, sem duplicar a lista).
  const legendContent = (
    <Box sx={{
      display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap',
      flex: '1 1 320px', px: 0.5, py: 1,
    }}>
      {featureTypes.map((f) => (
        <Box key={f.id ?? f.name} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: f.color || '#9BA3AF', flexShrink: 0 }} />
          <Typography variant="caption" sx={{ fontSize: '0.68rem', color: 'text.secondary', whiteSpace: 'nowrap' }}>
            {f.name}
          </Typography>
        </Box>
      ))}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#9BA3AF', flexShrink: 0 }} />
        <Typography variant="caption" sx={{ fontSize: '0.68rem', color: 'text.secondary', whiteSpace: 'nowrap' }}>Other</Typography>
      </Box>
    </Box>
  );

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', p: { xs: 2, md: 4 } }}>

      {pageToolbar}

      {/* ── Tabela (recebe as entries JÁ filtradas) ─────────────────────────── */}
      <TrackerTable
        entries={filteredEntries}
        loading={loading || loadingOne}
        onRowClick={handleRowClick}
        onDelete={handleDeleteFromTable}
        onCopy={handleCopy}
        showClosed={showClosed}
        canEditEntry={canEditEntry}
        canDeleteEntry={canDeleteEntry}
        userRole={role}
        userId={userId}
        maximizedHeader={pageToolbar}
      />

      {/* ── Divisor entre tabela e Gantt/Calendar ───────────────────────────── */}
      <Box sx={{ display: 'flex', alignItems: 'center', mt: 4, mb: 0, gap: 2 }}>
        <Box sx={{ flex: 1, height: '1px', bgcolor: 'divider' }} />
        <Typography variant="caption" color="text.disabled" sx={{ textTransform: 'uppercase', letterSpacing: 1, fontSize: '0.62rem', fontWeight: 700, whiteSpace: 'nowrap' }}>
          {chartView === 'gantt' ? 'Gantt Chart' : 'Calendar View'}
        </Typography>
        <Box sx={{ flex: 1, height: '1px', bgcolor: 'divider' }} />
      </Box>

      {/* ── Legenda (dinâmica, a partir de featureTypes — mesma fonte de
          dados/cores do Gantt e do Calendar, editável em Tracker Settings >
          Type) + Toggle Gantt/Calendar, na mesma linha. Antes o toggle
          ficava sobreposto à legenda com margem negativa, partindo do
          princípio de que a legenda cabia sempre numa única linha — deixou
          de ser verdade assim que há mais do que ~7 Types e a legenda
          quebra para 2 linhas. Agora são dois irmãos no mesmo flex row,
          sem sobreposição, e a legenda quebra por baixo de si própria sem
          nunca invadir o toggle. ──────────────────────────────────────── */}
      <Box sx={{
        display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
        flexWrap: 'wrap', gap: 1.5, mt: 1.5,
      }}>
        {legendContent}

        <Box sx={{ display: 'flex', bgcolor: '#f0f0f0', borderRadius: '8px', p: '3px', gap: '2px', flexShrink: 0 }}>
          {[
            { value: 'gantt',    icon: <TimelineIcon sx={{ fontSize: 13 }} />,      label: 'Gantt' },
            { value: 'calendar', icon: <CalendarMonthIcon sx={{ fontSize: 13 }} />, label: 'Calendar' },
          ].map(({ value, icon, label }) => (
            <Box
              key={value}
              onClick={() => setChartView(value)}
              sx={{
                display: 'flex', alignItems: 'center', gap: 0.5,
                px: 1.4, py: 0.45, borderRadius: '6px',
                cursor: 'pointer', userSelect: 'none',
                transition: 'all 0.15s ease',
                color: chartView === value ? 'primary.main' : 'text.secondary',
                bgcolor: chartView === value ? '#ffffff' : 'transparent',
                boxShadow: chartView === value ? '0 1px 3px rgba(0,0,0,0.12)' : 'none',
              }}
            >
              {icon}
              <Typography variant="button" sx={{ lineHeight: 1, fontSize: '0.75rem' }}>{label}</Typography>
            </Box>
          ))}
        </Box>
      </Box>
      {chartView === 'gantt' ? (
        <TrackerGantt
          entries={filteredEntries}
          onRowClick={handleRowClick}
          showClosed={showClosed}
          featureTypes={featureTypes}
          legend={legendContent}
        />
      ) : (
        <TrackerCalendar
          entries={filteredEntries}
          featureTypes={featureTypes}
          onRowClick={handleRowClick}
          showClosed={showClosed}
        />
      )}

      {/* ── Modal de criar/editar ───────────────────────────────────────────── */}
      <TrackerModal
        key={lookupsVersion}
        open={modalOpen}
        entry={activeEntry}
        saving={saving}
        loadingOne={loadingOne}
        onSave={handleSave}
        onClose={handleCloseModal}
        onDelete={handleDeleteFromModal}
        onOpenSiteList={handleOpenSiteList}
        userRole={role}
        userId={userId}
        canDeleteEntry={canDeleteEntry}
        privilegeVendor={vendor}
      />

      <TrackerSettings
        open={settingsOpen}
        onClose={() => { setSettingsOpen(false); setLookupsVersion((v) => v + 1); }}
      />

      <BulkImportDialog
        open={bulkImportOpen}
        onClose={() => setBulkImportOpen(false)}
        onImported={(result) => {
          showSnack('Activity created from the imported file.');
          dispatch(loadEntries(vendor));

          // Abre automaticamente a atividade acabada de criar, para o
          // utilizador poder rever/podar o Site List sem ter de a procurar
          // na tabela — só se tiver deixado marcada a opção "Auto-open
          // after creation" no Bulk Import.
          //
          // IMPORTANTE: ao contrário de handleRowClick (que já tem a linha
          // completa da tabela para mostrar de imediato), aqui só temos o
          // id — a atividade acabou de ser criada e ainda não está na
          // lista local. Por isso NÃO fazemos dispatch(openModal({ id }))
          // antes do loadEntry: isso punha modalOpen=true com um entry
          // praticamente vazio ({ id } apenas) ANTES de loadingOne passar a
          // true, e o TrackerModal calculava entryReady = !loadingOne como
          // true nesse instante — tentava renderizar o formulário completo
          // com technology/vendor/servers todos undefined. loadEntry por si
          // só já abre o modal (FETCH_ONE_SUCCESS define modalOpen=true) só
          // quando os dados completos chegam, sem esse render intermédio
          // partido.
          if (result?.entryId && result?.autoOpen) {
            dispatch(loadEntry(result.entryId));
          }
        }}
      />

      {/*
        SiteListDialog renderizado aqui, ao lado do TrackerModal — nunca dentro.
        Assim os dois Dialogs são independentes no DOM e o fecho de um
        nunca afecta o outro.
      */}
      <SiteListDialog
        open={siteListOpen}
        server={siteListServer}
        onClose={handleCloseSiteList}
        onConfirm={handleConfirmSiteList}
      />

      {/* ── Snackbar de feedback ─────────────────────────────────────────────── */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={closeSnack}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={snackbar.severity} variant="filled" sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>

    </Box>
  );
};

export default TrackerPage;